import { Input, Popover, Space, Tag, Typography } from 'antd';
import type { InputRef } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AddIcon as PlusOutlined, SearchIcon as SearchOutlined } from '@/icons';

const { Text } = Typography;

/**
 * Capitalise the first letter of every word.
 */
const toTitleCase = (text: string): string =>
  text.replaceAll(/\b\w/g, (char: string) => char.toUpperCase());

/**
 * Single-segment skill tags: no `/` hierarchy. If the user types `A/B`, only `A` is kept.
 */
export const normalizeFlatSkillTag = (value: string): string => {
  const firstSegment = value.replace(/^#+/, '').split(/[/,]/u)[0] ?? '';
  return toTitleCase(
    firstSegment
      .replaceAll(/[^a-zA-Z0-9 ]/g, '')
      .replaceAll(/\s+/g, ' ')
      .trim(),
  );
};

/** Returns a lowercase, trimmed key used for deduplication comparisons. */
const toKey = (value: string): string => value.trim().toLowerCase();

/** Props for the `SkillTagManager` flat-tag chip editor. */
export interface SkillTagManagerProps {
  tags: string[];
  onTagsChange: (tags: string[]) => void;
}

/**
 * Local-only tag chips for skills (persisted with the skill version). One level only.
 */
export const SkillTagManager: React.FC<SkillTagManagerProps> = ({ tags, onTagsChange }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);
  const inputRef = useRef<InputRef>(null);

  useEffect(() => {
    if (dropdownOpen) {
      const timer = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(timer);
    }
  }, [dropdownOpen]);

  useEffect(() => {
    if (!dropdownOpen) {
      setSearchValue('');
      setHighlightIndex(0);
    }
  }, [dropdownOpen]);

  const normalized = useMemo(() => normalizeFlatSkillTag(searchValue), [searchValue]);

  const showCreateOption = useMemo(() => {
    if (normalized.length === 0) {
      return false;
    }
    const k = toKey(normalized);
    return !tags.some((t) => toKey(t) === k);
  }, [normalized, tags]);

  /** Normalizes, deduplicates, and appends a new tag then closes the dropdown. */
  const addTag = useCallback(
    (raw: string) => {
      const nextName = normalizeFlatSkillTag(raw);
      if (!nextName) {
        return;
      }
      const k = toKey(nextName);
      if (tags.some((t) => toKey(t) === k)) {
        setSearchValue('');
        setDropdownOpen(false);
        return;
      }
      onTagsChange([...tags, nextName]);
      setSearchValue('');
      setDropdownOpen(false);
    },
    [tags, onTagsChange],
  );

  /** Removes the given tag from the current set. */
  const removeTag = useCallback(
    (tagName: string) => {
      onTagsChange(tags.filter((t) => t !== tagName));
    },
    [tags, onTagsChange],
  );

  /** Handles Enter/Tab to confirm tag input and Escape to close the dropdown. */
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        if (normalized.length > 0) {
          addTag(searchValue);
        }
        return;
      }
      if (event.key === 'Escape') {
        setDropdownOpen(false);
      }
    },
    [addTag, normalized.length, searchValue],
  );

  const dropdownContent = (
    <div className="tag-manager__dropdown" style={{ width: 300 }}>
      <Input
        ref={inputRef}
        aria-label="Search or create skill tag"
        placeholder="Add a tag"
        prefix={<SearchOutlined style={{ color: '#999' }} aria-hidden />}
        value={searchValue}
        onChange={(e) => {
          setSearchValue(e.target.value);
          setHighlightIndex(0);
        }}
        onKeyDown={handleKeyDown}
        style={{ marginBottom: 8 }}
      />

      <div aria-label="Skill tag actions" style={{ maxHeight: 120, overflowY: 'auto' }}>
        {showCreateOption && (
          <button
            type="button"
            aria-label={
              highlightIndex === 0 ? `Use tag ${normalized} (highlighted)` : `Use tag ${normalized}`
            }
            className="tag-manager__option"
            style={{
              width: '100%',
              border: 'none',
              textAlign: 'left',
              padding: '6px 8px',
              cursor: 'pointer',
              borderRadius: 4,
              background: highlightIndex === 0 ? 'rgba(0,0,0,0.06)' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
            onClick={() => addTag(searchValue)}
            onMouseEnter={() => setHighlightIndex(0)}
          >
            <PlusOutlined style={{ fontSize: 12 }} aria-hidden />
            <Text>
              Add <Text strong>{normalized}</Text>
            </Text>
          </button>
        )}

        {!showCreateOption && normalized.length > 0 && (
          <Text type="secondary" style={{ display: 'block', padding: '8px', textAlign: 'center' }}>
            Tag already added
          </Text>
        )}
      </div>

      <Text
        type="secondary"
        style={{ display: 'block', fontSize: 12, marginTop: 8, textAlign: 'center' }}
      >
        Skill tags are flat — <Text code>/</Text> is not allowed (only the first word is kept).
      </Text>
    </div>
  );

  return (
    <Space wrap size={[8, 8]} className="tag-manager" style={{ width: '100%' }}>
      {tags.map((tagName) => (
        <Tag
          key={tagName}
          closable
          onClose={(event) => {
            event.preventDefault();
            removeTag(tagName);
          }}
        >
          {tagName}
        </Tag>
      ))}

      <Popover
        content={dropdownContent}
        trigger="click"
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
        placement="bottomLeft"
        arrow={false}
      >
        <Tag
          className="tag-manager__add-btn"
          style={{ borderStyle: 'dashed', cursor: 'pointer' }}
          aria-label="Add skill tag"
          aria-haspopup="listbox"
          onClick={() => setDropdownOpen(true)}
        >
          <PlusOutlined aria-hidden /> Add tag
        </Tag>
      </Popover>
    </Space>
  );
};
